import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchAdminCompetitionEvent } from '../../api/admin/api';
import { deleteCompetitionTeam, bulkDeleteCompetitionTeams } from '../../api/api';
import Stack from '../../components/Stack';
import Card from '../../components/Card';
import Header from '../../components/Header';
import ActiveBadge from '../../components/ActiveBadge';
import Button from '../../components/Button';
import KebabMenu from '../../components/KebabMenu';

export default function CompetitionEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);

  // UI state
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set()); // expanded team IDs
  const [search, setSearch] = useState(''); // search text

  const toggleExpanded = (teamId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(teamId) ? next.delete(teamId) : next.add(teamId);
      return next;
    });
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await deleteCompetitionTeam(teamId);
      setEventData((prev) => ({
        ...prev,
        competition_teams: prev.competition_teams.filter((t) => t.id !== teamId),
      }));
      setExpanded((prev) => {
        const n = new Set(prev);
        n.delete(teamId);
        return n;
      });
    } catch (err) {
      console.error(err);
      alert('Failed to delete team: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTeamIds.length === 0) return;
    if (!window.confirm('Are you sure you want to delete selected teams?')) return;
    try {
      await bulkDeleteCompetitionTeams(selectedTeamIds);
      setEventData((prev) => ({
        ...prev,
        competition_teams: prev.competition_teams.filter((t) => !selectedTeamIds.includes(t.id)),
      }));
      setSelectedTeamIds([]);
      setBulkDeleteMode(false);
      setExpanded((prev) => {
        const n = new Set(prev);
        selectedTeamIds.forEach((tid) => n.delete(tid));
        return n;
      });
    } catch (err) {
      console.error(err);
      alert('Failed to delete selected teams');
    }
  };

  const navigateToCreateTeam = () => {
    navigate(`/admin/competitions/${eventData.competition_id}/competition_events/${id}/competition_team/${false}/null`);
  };

  useEffect(() => {
    fetchAdminCompetitionEvent(id).then((res) => {
      setEventData(res);
      setLoading(false);
    });
  }, [id]);

  if (loading) return <div className="p-8">Loading...</div>;
  if (!eventData) return <div className="p-8 text-red-500">Event not found.</div>;

  const { start_date, end_date, is_active, competition, competition_teams, registration_starts_at, registration_ends_at, name = [] } = eventData;
  const teams = competition_teams;

  // Filter by team name (case-insensitive)
  const displayedTeams = teams.filter((t) =>
    (t.name ?? '').toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <Card className="gap-2">
      <Stack width="full" align="start" gap="4" className="border-b border-gray-200 pb-2 sm:pb-6 sm:gap-8">
        <Stack width="full" direction="row" justify="between" align="center" gap="1">
          <Stack width="full" direction="row" justify="start" align="center" gap="1">
            <Header size="xl">{competition.name}: {name}</Header>
            <ActiveBadge value={is_active}>{is_active ? 'Active' : 'Inactive'}</ActiveBadge>
          </Stack>
          <Button
            onClickMethod={() => navigate(`/admin/competitions/${eventData.competition_id}`)}
            color="gray"
            className="text-xs"
          >
            Back to Competition
          </Button>
        </Stack>

        <Stack width="full" justify="start" align="start">
          <Stack width="full" direction="row" justify="start" align="center" gap="1">
            <p className="text-xs">
              <strong>Registration Start Date:</strong>
            </p>
            <p className="text-xs">
              {registration_starts_at}
            </p>
          </Stack>
          <Stack width="full" direction="row" justify="start" align="center" gap="1">
            <p className="text-xs">
              <strong>Registration End Date:</strong>
            </p>
            <p className="text-xs">
              {registration_ends_at}
            </p>
          </Stack>
          <Stack width="full" direction="row" justify="start" align="center" gap="1">
            <p className="text-xs">
              <strong>Start Date:</strong>
            </p>
            <p className="text-xs">
              {start_date}
            </p>
          </Stack>
          <Stack width="full" direction="row" justify="start" align="center" gap="1">
            <p className="text-xs">
              <strong>End Date:</strong>
            </p>
            <p className="text-xs">
              {end_date}
            </p>
          </Stack>
        </Stack>

        <div>
          <p className="text-sm">
            <strong>Total Teams:</strong> {teams.length}
          </p>
        </div>
      </Stack>

      <Stack width="full" justify="start" align="start" gap="8" className="pt-1 sm:pt-6">
        <div className='w-full flex flex-col gap-2 justify-start items-start sm:flex-row sm:justify-between sm:gap-4'>
          <Stack width="auto" justify="start" align="start" gap="2">
            <Header>Competition Teams</Header>
            <p className="text-sm text-gray-600">
              Manage the teams participating in this competition event.
            </p>
          </Stack>

          <Stack width="auto" direction="row" justify="end" align="center" gap="2">
            <div className="flex gap-2">
              {bulkDeleteMode && (
                <Button
                  onClickMethod={handleBulkDelete}
                  color="red"
                  className="text-xs"
                  disabled={selectedTeamIds.length === 0}
                >
                  Delete Selected
                </Button>
              )}
              <Button
                onClickMethod={() => {
                  setBulkDeleteMode(!bulkDeleteMode);
                  setSelectedTeamIds([]);
                }}
                color={bulkDeleteMode ? 'gray' : 'black_bordered'}
                className="text-xs"
              >
                {bulkDeleteMode ? 'Done' : 'Manage Teams'}
              </Button>
            </div>

            {!bulkDeleteMode && (
              <Button onClickMethod={navigateToCreateTeam} color="yellow" className="text-xs">
                Create New Team
              </Button>
            )}
          </Stack>
        </div>


        {/* Team Cards (filtered) */}
        <div className="w-full max-h-[60vh] overflow-y-auto space-y-4 pr-1">
          {/* Sticky search header */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-gray-200 py-2">
            <div className="relative w-full">
              {/* magnifying glass icon */}
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teams by name…"
                className="w-full pl-10 pr-9 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label="Clear search"
                  title="Clear"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M9 9l6 6m0-6l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>

            <span className="mt-1 block text-xs text-gray-500">
              Showing {displayedTeams.length} of {teams.length}
            </span>
          </div>

          {teams.length === 0 && <p className="text-gray-500">No teams registered yet.</p>}
          {teams.length > 0 && displayedTeams.length === 0 && (
            <p className="text-gray-500">No teams match “{search}”.</p>
          )}
          
          {displayedTeams.map((team) => {
            const memberCount = team.competition_team_members?.length || 0;
            const isExpanded = expanded.has(team.id);

            const division = team.division || team.division_name || 'N/A';
            const office = team.office || team.office_name || 'N/A';
            const address = team.address || 'N/A';
            const creator = team.team_creator;

            return (
              <div key={team.id} className="rounded-md border border-gray-200 bg-white shadow-sm">
                {/* Card Header Row */}
                <button
                  type="button"
                  onClick={() => toggleExpanded(team.id)}
                  className="w-full text-left p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {bulkDeleteMode && (
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedTeamIds.includes(team.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            const checked = e.target.checked;
                            setSelectedTeamIds((prev) =>
                              checked ? [...prev, team.id] : prev.filter((tid) => tid !== team.id)
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold">{team.name}</h3>
                        </div>

                        {/* Meta lines */}
                        <div className="mt-1 space-y-0.5 text-xs text-gray-700">
                          <div>
                            <strong>Division:</strong> {division}
                          </div>
                          <div>
                            <strong>Office:</strong> {office}
                          </div>
                          <div>
                            <strong>Address:</strong> {address}
                          </div>
                          <div className="flex gap-1">
                            <strong>Creator:</strong>
                            {creator && <p>{creator.name}</p>}
                          </div>
                          <div>
                            <strong>Team Members:</strong> {memberCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          team.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {team.is_active ? 'Active' : 'Inactive'}
                      </span>

                      <KebabMenu
                        onEdit={() =>
                          navigate(
                            `/admin/competitions/${eventData.competition_id}/competition_events/${id}/competition_team/${true}/${team.id}`
                          )
                        }
                        onDelete={() => handleDeleteTeam(team.id)}
                      />
                    </div>
                  </div>
                </button>

                {/* Expanded body */}
                {isExpanded && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold mb-2">Team Members</h4>
                      {memberCount === 0 ? (
                        <p className="text-sm text-gray-500">No members yet.</p>
                      ) : (
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          {team.competition_team_members.map((m) => {
                            const name = m.name || '';
                            const role = m.role || '';
                            const officeName = m.office || m.office_name || '';
                            return (
                              <li key={m.id}>
                                <span className="font-medium">{name}</span>
                                {role && ` (${role})`}
                                {officeName && ` — ${officeName}`}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Stack>
    </Card>
  );
}
